import React from 'react';
import { Toaster, toast as sonnerToast } from 'sonner';
import useTheme from '@dashboard/hooks/useTheme';

/**
 * @param {Object} params
 * @param {React.ReactNode} params.title
 * @param {string} [params.description]
 * @param {'success' | 'error' | 'alert'} [params.type]
 * @param {number} [params.duration]
 */
function toast({ title, description, type = 'success', duration = 2000 }) {
  const options = { description, duration };

  switch (type) {
    case 'error':
      sonnerToast.error(title, options);
      break;
    case 'alert':
      sonnerToast.warning(title, options);
      break;
    case 'success':
    default:
      sonnerToast.success(title, options);
      break;
  }
}

function DashboardToaster() {
  const { appliedTheme } = useTheme();
  return (
    <Toaster
      theme={appliedTheme}
      position="top-center"
      icons={{
        success: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="m9 12 2 2 4-4" />
          </svg>
        ),
        error: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="m15 9-6 6" />
            <path d="m9 9 6 6" />
          </svg>
        ),
        warning: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
          </svg>
        ),
        info: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
        ),
      }}
      toastOptions={{
        classNames: {
          toast:
            'group toast group-[.toaster]:bg-white dark:group-[.toaster]:bg-popover group-[.toaster]:text-gray-950 dark:group-[.toaster]:text-popover-foreground group-[.toaster]:border-gray-200 dark:group-[.toaster]:border-border group-[.toaster]:shadow-lg group-[.toaster]:rounded-lg',
          description: 'group-[.toast]:text-gray-500 dark:group-[.toast]:text-muted-foreground',
          actionButton:
            'group-[.toast]:bg-gray-900 dark:group-[.toast]:bg-primary group-[.toast]:text-gray-50 dark:group-[.toast]:text-primary-foreground',
          cancelButton:
            'group-[.toast]:bg-gray-100 dark:group-[.toast]:bg-muted group-[.toast]:text-gray-500 dark:group-[.toast]:text-muted-foreground',
          success: 'group-[.toaster]:!text-gray-950 dark:group-[.toaster]:!text-popover-foreground',
          error: 'group-[.toaster]:!text-gray-950 dark:group-[.toaster]:!text-popover-foreground',
          warning: 'group-[.toaster]:!text-gray-950 dark:group-[.toaster]:!text-popover-foreground',
        },
      }}
    />
  );
}

export { DashboardToaster, toast };
